import type { components } from '@/lib/api-types';

/** The coordinates Google gave, with the time they may be kept until (30 days, address service). */
export type AddressCoordinates = components['schemas']['AddressTimedCoordinates'];

/**
 * The address the component takes and returns (#958, report §3.11). It knows nothing of who uses
 * it: a supplier's site, an organisation, a person. Every text part is a string, `''` when it is
 * missing, so a form can save whatever is there (board 2.4). `placeId` and `coordinates` are set
 * only by a chosen suggestion, and dropped when the person moves the address somewhere else by
 * hand, so they always describe what the fields say.
 */
export interface AddressValue {
  readonly street: string;
  readonly streetNumber: string;
  readonly postalCode: string;
  readonly city: string;
  /** In Italy the two-letter code (`PD`); elsewhere the region, county or state, when used. */
  readonly province: string;
  /** ISO 3166-1 alpha-2, `''` when not known. */
  readonly countryCode: string;
  /** Google's place id: may be kept indefinitely. */
  readonly placeId: string | null;
  /** May be kept until `keepUntil`, then fetched again from the place id. */
  readonly coordinates: AddressCoordinates | null;
}

export type AddressPart =
  'street' | 'streetNumber' | 'postalCode' | 'city' | 'province' | 'countryCode';

export const EMPTY_ADDRESS: AddressValue = {
  street: '',
  streetNumber: '',
  postalCode: '',
  city: '',
  province: '',
  countryCode: 'IT',
  placeId: null,
  coordinates: null,
};

/** The structured address of a chosen suggestion, as the field shows it. */
export function addressFromPlace(
  place: components['schemas']['StructuredAddressDto'],
): AddressValue {
  return {
    street: place.street ?? '',
    streetNumber: place.streetNumber ?? '',
    postalCode: place.postalCode ?? '',
    city: place.city ?? '',
    province: place.province ?? '',
    countryCode: place.countryCode ?? '',
    placeId: place.placeId,
    coordinates: place.coordinates,
  };
}

/** The parts that place the address: changing one by hand moves it away from Google's place. */
const LOCATING_PARTS: ReadonlySet<AddressPart> = new Set([
  'street',
  'streetNumber',
  'postalCode',
  'city',
  'countryCode',
]);

const same = (a: string, b: string) =>
  a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();

/**
 * One part edited by hand. Filling in a part the place lacked (the house number of board 2.4) or
 * rewriting the province keeps the place id and the coordinates; changing a part the place had
 * drops them, because they would no longer describe what is written.
 */
export function editAddressPart(
  value: AddressValue,
  part: AddressPart,
  text: string,
  place: AddressValue | null,
): AddressValue {
  const next: AddressValue = { ...value, [part]: text };
  if (value.placeId === null || !LOCATING_PARTS.has(part)) {
    return next;
  }
  const original = place !== null && place.placeId === value.placeId ? place[part] : value[part];
  if (original.trim() === '' || same(original, text)) {
    return next;
  }
  return { ...next, placeId: null, coordinates: null };
}

/** The parts a chosen place lacks, in the order the form shows them (board 2.4). */
export function missingParts(value: AddressValue): AddressPart[] {
  const parts: AddressPart[] = ['street', 'streetNumber', 'postalCode', 'city'];
  if (value.countryCode === 'IT') {
    parts.push('province');
  }
  parts.push('countryCode');
  return parts.filter((part) => value[part].trim() === '');
}

export function isAddressEmpty(value: AddressValue): boolean {
  return (['street', 'streetNumber', 'postalCode', 'city', 'province'] as const).every(
    (part) => value[part].trim() === '',
  );
}

/**
 * How a country writes its address (board 2.3): where the house number goes, whether the postal
 * code follows the city, and what the province is called. Italy, and any country not listed,
 * keep the Italian order.
 */
export interface AddressFormat {
  readonly numberFirst: boolean;
  readonly postalCodeAfterCity: boolean;
  readonly region: 'province' | 'county' | 'state' | 'region';
}

const NUMBER_FIRST = new Set(['GB', 'IE', 'US', 'CA', 'AU', 'NZ', 'FR', 'LU', 'MT']);
const POSTAL_CODE_AFTER_CITY = new Set(['GB', 'IE', 'US', 'CA', 'AU', 'NZ', 'MT']);
const COUNTY = new Set(['GB', 'IE']);
const STATE = new Set(['US', 'AU', 'BR', 'MX', 'IN', 'DE', 'AT']);

export function addressFormat(countryCode: string): AddressFormat {
  return {
    numberFirst: NUMBER_FIRST.has(countryCode),
    postalCodeAfterCity: POSTAL_CODE_AFTER_CITY.has(countryCode),
    region:
      countryCode === 'IT'
        ? 'province'
        : COUNTY.has(countryCode)
          ? 'county'
          : STATE.has(countryCode)
            ? 'state'
            : 'region',
  };
}
